import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { API_ENDPOINTS, getImageUrl } from '../../utils/GlobalAPI';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import HireMeForm from './HireMeForm';

const PremiumInfluencer = () => {
  const [sliderKey, setSliderKey] = useState(0);
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHireForm, setShowHireForm] = useState(false);
  const [selectedInfluencer, setSelectedInfluencer] = useState(null);

  // Fetch influencers from backend
  useEffect(() => {
    const fetchInfluencers = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.PREMIUM_INFLUENCERS);
        const data = await response.json();
        
        // Validate that data is an array
        if (!Array.isArray(data)) {
          console.error('Expected array of influencers but got:', typeof data);
          setInfluencers([]);
        } else {
          setInfluencers(data);
        }
      } catch (error) {
        console.error('Error fetching influencers:', error);
        setInfluencers([]); // Set empty array on error
      } finally {
        setLoading(false);
      }
    };

    fetchInfluencers();
  }, []);

  // Force re-render on resize to fix responsive bugs
  useEffect(() => {
    const handleResize = () => setSliderKey(prev => prev + 1);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    document.body.style.overflow = showHireForm ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [showHireForm]);

  const handleHireClick = (influencer) => {
    setSelectedInfluencer(influencer);
    setShowHireForm(true);
  };

  // Convert premium influencer to format that HireMeForm expects
  const convertToInfluencerData = (premiumInf) => {
    if (!premiumInf) return null;
    return {
      influencerName: premiumInf.name,
      name: premiumInf.name,
      bio: premiumInf.bio,
      influencerImage: premiumInf.image,
      instaSubs: premiumInf.followers,
      facebook: premiumInf.facebook,
      twitter: premiumInf.twitter,
      engagementPercentage: premiumInf.engagement?.replace('%', '') || premiumInf.engagement,
      averageViews: premiumInf.avgViews,
      platform: premiumInf.platform,
      clients: Array.isArray(premiumInf.clients) ? premiumInf.clients : [],
      category: 'Premium',
      influencerType: 'Premium Influencer'
    };
  };

  const settings = {
    dots: true,
    infinite: true,
    speed: 600,
    autoplay: true,
    autoplaySpeed: 4000,
    arrows: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
    pauseOnHover: true,
    swipe: true,
    draggable: true,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  if (loading) {
    return (
      <section className="w-full px-4 py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto text-center">
          <div className="text-xl text-gray-600">Loading premium influencers...</div>
        </div>
      </section>
    );
  }

  if (influencers.length === 0) {
    return (
      <section className="w-full px-4 py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-gray-800">Meet Our Premium Influencers</h2>
          <p className="text-gray-500 mt-3">No premium influencers available at the moment.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full px-4 py-12 bg-gray-50 font-[Montserrat]">
      <div className="max-w-6xl mx-auto text-center mb-10">
        <h2 className="text-3xl md:text-4xl font-semibold text-gray-800">Meet Our Premium Influencers</h2>
        <p className="text-gray-500 mt-3">
          Discover top talent with proven engagement, global reach, and industry-leading partnerships.
        </p>
      </div>

      <div className="max-w-5xl mx-auto">
        <Slider key={sliderKey} {...settings}>
          {influencers.map((inf, idx) => (
            <div key={idx}>
              <div className="bg-white shadow-xl rounded-2xl p-6 md:p-10 flex flex-col md:flex-row items-center gap-8 transition-all duration-300 min-h-[360px]">
                <img
                  src={getImageUrl(inf.image)}
                  alt={inf.name}
                  className="w-40 h-40 rounded-full object-cover border-4 border-blue-100 shadow-md"
                />
                <div className="text-left w-full">
                  <h3 className="text-2xl font-bold text-gray-800">{inf.name}</h3>
                  <p className="text-gray-600 mt-2">{inf.bio}</p>

                            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-sm text-gray-700">
                              <div><span className="font-semibold">{inf.followers}</span> Followers</div>
                              <div><span className="font-semibold">{inf.engagement}%</span> Engagement</div>
                              <div><span className="font-semibold">{inf.avgViews}/Views</span> Avg Views</div>
                              <div className="col-span-2 sm:col-span-1"><span className="font-semibold">{inf.platform}</span></div>
                            </div>
                            
                            <div className="mt-4 flex gap-3 text-gray-600">
                              {inf.facebook && (
                                <a href={inf.facebook.includes('http') ? inf.facebook : `https://${inf.facebook}`} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 transition-colors">
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z"/></svg>
                                </a>
                              )}
                              {inf.twitter && (
                                <a href={inf.twitter.includes('http') ? inf.twitter : `https://${inf.twitter}`} target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.95 4.57c-.88.39-1.83.65-2.83.77 1.02-.61 1.8-1.58 2.17-2.73-.95.56-2.01.97-3.13 1.19-.9-.96-2.18-1.56-3.59-1.56-2.71 0-4.92 2.21-4.92 4.92 0 .39.04.77.13 1.13-4.09-.21-7.72-2.16-10.15-5.14-.42.72-.66 1.56-.66 2.47 0 1.71.87 3.22 2.19 4.1-.8-.03-1.56-.25-2.22-.61v.06c0 2.38 1.69 4.36 3.94 4.81-.41.11-.85.17-1.3.17-.32 0-.62-.03-.92-.09.63 1.95 2.44 3.37 4.59 3.41-1.68 1.32-3.8 2.11-6.11 2.11-.4 0-.79-.02-1.18-.07 2.17 1.39 4.76 2.2 7.55 2.2 9.06 0 14.01-7.51 14.01-14.01 0-.21-.01-.42-.03-.63.96-.69 1.8-1.56 2.46-2.55z"/></svg>
                                </a>
                              )}
                            </div>

                  <div className="mt-5">
                    <p className="font-semibold text-gray-800 mb-2">Worked with:</p>
                    <div className="flex flex-wrap gap-2">
                      {inf.clients.map((client) => (
                        <span
                          key={client}
                          className="bg-blue-100 text-blue-800 px-3 py-1 text-xs rounded-full font-medium"
                        >
                          {client}
                        </span>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={() => handleHireClick(inf)}
                    className="inline-block mt-6 px-6 py-3 bg-blue-600 text-white text-sm font-semibold rounded-xl shadow-md hover:bg-blue-700 transition-all duration-300 transform hover:scale-105"
                  >
                    Hire Me
                  </button>
                </div>
              </div>
            </div>
          ))}
        </Slider>
      </div>

      {/* Hire Me Form Modal */}
      {typeof window !== 'undefined' && createPortal(
        <AnimatePresence>
          {showHireForm && selectedInfluencer && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[102] flex items-start justify-center p-2 sm:p-4 bg-black/30 backdrop-blur-sm overflow-y-auto py-8"
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  setShowHireForm(false);
                  setSelectedInfluencer(null);
                }
              }}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                className="w-full max-w-xs sm:max-w-sm md:max-w-2xl lg:max-w-4xl xl:max-w-5xl bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh]"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="bg-gradient-to-r from-blue-600 to-cyan-500 px-6 py-4 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <h2 className="text-xl font-semibold text-white">Hire Premium Creator</h2>
                    <span className="bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold">
                      PREMIUM
                    </span>
                  </div>
                  <motion.button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowHireForm(false);
                      setSelectedInfluencer(null);
                    }}
                    className="p-2 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </motion.button>
                </div>
                <div className="p-0 sm:p-2 lg:p-6 flex-1 overflow-hidden">
                  <HireMeForm
                    influencerId={selectedInfluencer._id}
                    influencerData={convertToInfluencerData(selectedInfluencer)}
                    isPremiumInfluencer={true}
                    premiumInfluencerId={selectedInfluencer._id}
                    onClose={() => {
                      setShowHireForm(false);
                      setSelectedInfluencer(null);
                    }}
                  />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </section>
  );
};

export default PremiumInfluencer;
